// Hooks para la docs page — listar topics y cargar el markdown de cada
// topic. Backend: GET /api/v1/docs y GET /api/v1/docs/:slug.

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface DocTopicMeta {
  slug: string;
  title: string;
  group: string;
  order: number;
}

export interface DocContent {
  slug: string;
  title: string;
  group: string;
  content: string;
}

export interface DocTopicGroup {
  group: string;
  topics: DocTopicMeta[];
}

export function useDocsIndex() {
  return useQuery({
    queryKey: ['docs', 'index'],
    queryFn: () => api.get<{ topics: DocTopicMeta[] }>('/api/v1/docs'),
    // Los topics no cambian en runtime — cache largo.
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Agrupa topics por `group` preservando el `order` interno y un orden de
 * grupos predefinido (los grupos del backend salen en orden de aparicion).
 */
export function groupTopics(topics: DocTopicMeta[] | undefined): DocTopicGroup[] {
  if (!topics) return [];
  const groupMap = new Map<string, DocTopicMeta[]>();
  for (const t of topics) {
    if (!groupMap.has(t.group)) groupMap.set(t.group, []);
    // biome-ignore lint/style/noNonNullAssertion: just set above
    groupMap.get(t.group)!.push(t);
  }
  return Array.from(groupMap.entries()).map(([group, ts]) => ({
    group,
    topics: ts.slice().sort((a, b) => a.order - b.order),
  }));
}

export function useDocContent(slug: string | undefined) {
  return useQuery({
    queryKey: ['docs', 'content', slug],
    queryFn: () => api.get<DocContent>(`/api/v1/docs/${slug}`),
    enabled: typeof slug === 'string' && slug.length > 0,
    staleTime: 60 * 60 * 1000,
  });
}
